import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, ComponentFactoryResolver, ElementRef, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { UserManagerService } from 'src/services/userManager/user-manager.service';
import { SkeletonServiceService } from 'src/services/skeleton/skeleton.service';
import { NavMenuComponent } from '../nav-menu/nav-menu.component';
import { OfferedLanguage } from 'src/models/language.model';
import { LanguageService } from 'src/services/language/language.service';
import { ThemeService } from 'src/services/theme/theme.service';
import { ConfigService } from 'src/services/config/config.service';
import { LocaleService } from 'src/services/locale/locale.service';
import { CurrencyFormatterPipe } from 'src/pipes/currency-formatter.pipe';
import { formatDateEn } from 'src/functions/date-format.function';
import { ArabicNumeralsPipe } from 'src/pipes/arabic-numerals/arabic-numerals.pipe';
declare var bootstrap: any;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [TranslateModule, RouterModule, AsyncPipe, CommonModule, NavMenuComponent, CurrencyFormatterPipe, ArabicNumeralsPipe],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class Header {

  @ViewChild('navMenuOffcanvas') navMenuOffcanvas!: ElementRef;
  @Input() isAuthentication = false;
  selectedLanguage: Observable<OfferedLanguage>;
  selectedDate: string;

  constructor(
    private userManagementService: UserManagerService,
    private skeletonServiceService: SkeletonServiceService,
    private viewCompRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private themeService: ThemeService,
    private configService: ConfigService,
    private localeService: LocaleService,
    private router: Router,
    private languageService: LanguageService
  ){
    this.selectedLanguage = skeletonServiceService.appLanguage$.asObservable();
    moment.locale(this.locale);
    this.selectedDate = formatDateEn();
  }

  get languagesOffered(): OfferedLanguage[] {
    return this.languageService.offeredLanguages;
  }

  /** Hidden when the host locked the language (iframe + valid ?lang=). */
  get showLanguageSwitcher(): boolean {
    return !this.languageService.languageLocked && this.languagesOffered.length > 1;
  }

  get theme() {
    return this.themeService.getTheme();
  }

  get userData() {
    return this.userManagementService.userData;
  }


  get isLoggedIn() {
    return Object.keys(this.userManagementService.userData ?? {}).includes('customer_ID') && !this.userManagementService.logingIn;
  }

  get isMobile() {
    return this.skeletonServiceService.isMobile();
  }

  get bonusCount() {
    return this.userManagementService.userData ? this.userManagementService.userData.balances.bon.length : 0;
  }

  get useAccCode() {
    return this.userManagementService.userData.isQuickSignup && !(this.userManagementService.userData.first_name && this.userManagementService.userData.last_name)
  }

  get partners() {
    return this.themeService.getTheme().partners;
  }

  get showUserManagementIcon() {
    return (this.theme.hasOwnProperty('userManagement') && !!this.theme.userManagement && this.theme.userManagement.env === 'external') || this.theme.isVirtual;
  }

  get locale() {
    return this.localeService.locale;
  }

  get formattedDate(): string {
    return moment(this.selectedDate, 'YYYY-MM-DD').locale(this.locale).format('dddd, D MMMM');
  }

  get headerNav() {
    return this.theme.headerNavMenu.filter((nav) => nav.channel.includes('desktop'));
  }

  get hasBonuses() {
    return this.configService.has_bonuses;
  }

  get isVirtual() {
    return this.configService.partnerOperation.enabled || this.theme.isVirtual;
  };


  openSideMenu() {
      const alertCompFactory = this.componentFactoryResolver.resolveComponentFactory(NavMenuComponent);
      const navMenuComponent = this.viewCompRef.createComponent(alertCompFactory);

      // navMenuComponent.instance.partners = this.partners;
      navMenuComponent.instance.closeSideMenu.subscribe((res)=> {
        if(res === true){
          navMenuComponent.instance.modalState = "leave";
        }
        navMenuComponent.destroy();
    })
    navMenuComponent.instance.langChange.subscribe((res: string) => {
      if (!!res) {
        this.selectLanguage(res)
      }
    })
  }

  openOffcanvas() {
    const offcanvas = new bootstrap.Offcanvas(this.navMenuOffcanvas.nativeElement);
    offcanvas.show();
  }

  hideCanvas() {
    if (this.navMenuOffcanvas) {
      const offcanvasInstance = bootstrap.Offcanvas.getOrCreateInstance(this.navMenuOffcanvas.nativeElement);
      offcanvasInstance.hide();
    }
  }

  selectLanguage(lang: string) {
    this.skeletonServiceService.switchLanguage(lang);
  }

  gotoUrl(){
    console.log("[userManagement.url]" , !!this.theme.userManagement?.url, this.userManagementService.setLoggedInUserUrlParams());
    
    if(!this.theme.userManagement?.url) {
      this.router.navigate(['/']);
      return;
    } else {
      let url = this.theme.userManagement?.url +'?' + this.userManagementService.setLoggedInUserUrlParams();
      url = this.configService.decorateUrl(url);
      window.location.href = url;
    }
  }

  goToPreviousDate() {
    this.selectedDate = formatDateEn(moment(this.selectedDate, 'YYYY-MM-DD').subtract(1, 'day'));
  }

  goToNextDate() {
    this.selectedDate = formatDateEn(moment(this.selectedDate, 'YYYY-MM-DD').add(1, 'day'));
  }

  onDateChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedDate = input.value;
  }

  navigateToLogin() {
    let otherStates = {};
    if(this.router.url.includes('/bet/selections')){
      otherStates = {
        redirectUrl: '/bet/selections',
        returnToPage: true,
      }
    }
    this.router.navigate(['/user/login'], {
      state: { hideHeader: false, ...otherStates }
    });
  }

  navigateToSignup() {
    const userManagement = this.theme.userManagement;

    if (!userManagement) {
      this.navigateToInternalSignup();
      return;
    }

    switch (userManagement.env) {
      case 'external':
        this.navigateToExternalSignup(userManagement.url);
        break;
      case 'internal':
      default:
        this.navigateToInternalSignup();
        break;
    }
  }

  private navigateToInternalSignup() {
    this.router.navigate(['/user/quick-signup'], {
      state: { hideHeader: false }
    });
  }

  private navigateToExternalSignup(baseUrl: string) {
    const signupPath = this.configService.quick_signup_enabled ? this.theme.userManagement?.entry.quickSignup : this.theme.userManagement?.entry.signup;
    window.location.href = `${baseUrl}/${signupPath}`;
  }

}
